'use client';

/**
 * The third root-layout banner (cutover note 01 E.4; spec §4.8; ruling 1):
 * shown to an administrator of a firm whose move support has requested and
 * nobody has agreed to, linking to the agreement page. Its shape is
 * `ScheduledDeactivationMemberBanner`'s, beside which it mounts.
 *
 * Publishes its height as `--banner-h-cutover` (wrapper is `flow-root` so the
 * StatusBanner's top margin is counted), which `SHELL_HEIGHT_CLASS` subtracts.
 * Not dismissible: the move waits on this agreement, and the banner goes the
 * moment it is given.
 */
import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRightLeft } from 'lucide-react';
import { AGREEMENT_PAGE_PATH } from '@/contexts/bms/cutover/agreement/domain/move-request-email';
import { StatusBanner, StatusBannerActions, StatusBannerContent, StatusBannerRow } from '@/components/ui/status-banner';
import { usePublishBannerHeight } from '@/shared/ui/use-publish-banner-height';
import { useCutoverBanner } from '../model/use-cutover-banner';
export function CutoverAgreementBanner() {
  const $ = _c(9);
  const hostRef = usePublishBannerHeight("cutover");
  const {
    shown,
    orgName
  } = useCutoverBanner();
  if (!shown) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ArrowRightLeft className="mt-0.5 h-5 w-5 shrink-0 text-white/80" aria-hidden={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = orgName !== null && orgName !== "" ? `${orgName}'s` : "your firm's";
  let t2;
  if ($[1] !== t1) {
    t2 = <StatusBannerContent className="flex items-start gap-3">{t0}<p className="text-sm font-medium text-white">Cova support has asked to move {t1} clients to Cova CRM. Nothing moves until you review and agree.</p></StatusBannerContent>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <StatusBannerActions><Link href={AGREEMENT_PAGE_PATH} className="inline-flex h-7 items-center rounded-md border border-white/30 bg-white/20 px-3 text-xs font-medium text-white backdrop-blur-xs hover:bg-white/30">Review and agree</Link></StatusBannerActions>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <StatusBanner color="purple" role="status"><StatusBannerRow>{t2}{t3}</StatusBannerRow></StatusBanner>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== hostRef || $[7] !== t4) {
    t5 = <div ref={hostRef} className="flow-root" data-testid="cutover-agreement-banner">{t4}</div>;
    $[6] = hostRef;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}